// External dependencies
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

// Internal dependencies
import AdminLayout from '@/modules/admin/client/components/AdminLayout.component';
import CmsAdminNav from '@/modules/cms/client/components/CmsAdminNav.component';
import {
  createFooterSection,
  deleteFooterSection,
  getFooterSections,
  updateFooterSection,
} from '@/modules/cms/client/api/admin-cms.api';

/**
 * Move an item within an array, returning a new array.
 *
 * @param {Array} list - Source array
 * @param {Number} from - Index to move
 * @param {Number} to - Destination index
 * @returns {Array}
 */
function move(list, from, to) {
  if (to < 0 || to >= list.length) {
    return list;
  }
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

/**
 * One editable section: name, enable toggle, its links, and ordering.
 */
function SectionEditor({ section, onSave, onDelete, onMove, saving }) {
  const [name, setName] = useState(section.name);
  const [enabled, setEnabled] = useState(section.enabled !== false);
  const [items, setItems] = useState(
    (section.items || []).map(item => ({
      label: item.label || '',
      url: item.url || '',
      enabled: item.enabled !== false,
    })),
  );

  const setItem = (index, field, value) => {
    setItems(previous =>
      previous.map((item, i) =>
        i === index ? { ...item, [field]: value } : item,
      ),
    );
  };

  const addItem = () => {
    setItems(previous => [...previous, { label: '', url: '', enabled: true }]);
  };

  const removeItem = index => {
    setItems(previous => previous.filter((item, i) => i !== index));
  };

  const handleSave = () => {
    onSave(section._id, {
      name,
      enabled,
      // Drop rows the editor left blank; order follows position.
      items: items
        .filter(item => item.label.trim() && item.url.trim())
        .map((item, index) => ({ ...item, order: index })),
    });
  };

  return (
    <div className="panel panel-default">
      <div className="panel-heading">
        <div className="row">
          <div className="col-sm-6">
            <input
              type="text"
              className="form-control"
              value={name}
              onChange={event => setName(event.target.value)}
              placeholder="Section name (e.g. Company)"
              aria-label="Section name"
            />
          </div>
          <div className="col-sm-6 text-right">
            <div
              className="checkbox"
              style={{ display: 'inline-block', marginRight: '10px' }}
            >
              <label>
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={event => setEnabled(event.target.checked)}
                />{' '}
                Shown
              </label>
            </div>
            <div
              className="btn-group"
              role="group"
              style={{ marginRight: '10px' }}
            >
              <button
                type="button"
                className="btn btn-default btn-sm"
                aria-label="Move section up"
                onClick={() => onMove(section._id, -1)}
              >
                ↑
              </button>
              <button
                type="button"
                className="btn btn-default btn-sm"
                aria-label="Move section down"
                onClick={() => onMove(section._id, 1)}
              >
                ↓
              </button>
            </div>
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={() => onDelete(section)}
            >
              Delete
            </button>
          </div>
        </div>
      </div>

      <div className="panel-body">
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: '35%' }}>Label</th>
              <th>Link</th>
              <th style={{ width: '90px' }}>Order</th>
              <th style={{ width: '70px' }} />
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={index}>
                <td>
                  <input
                    type="text"
                    className="form-control"
                    value={item.label}
                    onChange={event =>
                      setItem(index, 'label', event.target.value)
                    }
                    placeholder="Terms and Conditions"
                  />
                </td>
                <td>
                  <input
                    type="text"
                    className="form-control"
                    value={item.url}
                    onChange={event =>
                      setItem(index, 'url', event.target.value)
                    }
                    placeholder="/rules or https://…"
                  />
                </td>
                <td>
                  <div className="btn-group">
                    <button
                      type="button"
                      className="btn btn-default btn-xs"
                      aria-label="Move link up"
                      onClick={() => setItems(move(items, index, index - 1))}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="btn btn-default btn-xs"
                      aria-label="Move link down"
                      onClick={() => setItems(move(items, index, index + 1))}
                    >
                      ↓
                    </button>
                  </div>
                </td>
                <td>
                  <button
                    type="button"
                    className="btn btn-link btn-xs text-danger"
                    onClick={() => removeItem(index)}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
            {!items.length && (
              <tr>
                <td colSpan="4" className="text-muted">
                  No links yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <button
          type="button"
          className="btn btn-default btn-sm"
          onClick={addItem}
        >
          Add link
        </button>
        <button
          type="button"
          className="btn btn-primary btn-sm pull-right"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save section'}
        </button>
      </div>
    </div>
  );
}

SectionEditor.propTypes = {
  section: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
  saving: PropTypes.bool,
};

export default function AdminCmsFooterMenu() {
  const [sections, setSections] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [newName, setNewName] = useState('');

  const load = () => {
    setIsLoading(true);
    setError(null);

    getFooterSections()
      .then(setSections)
      .catch(() => setError('Failed to load the footer menu.'))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const flash = message => {
    setSuccess(message);
    setError(null);
    setTimeout(() => setSuccess(null), 2500);
  };

  const handleSave = async (id, payload) => {
    setSavingId(id);
    setError(null);

    try {
      const saved = await updateFooterSection(id, payload);
      setSections(previous =>
        previous.map(section => (section._id === id ? saved : section)),
      );
      flash('Section saved.');
    } catch (err) {
      const message =
        err.response && err.response.data && err.response.data.message;
      setError(message || 'Failed to save the section.');
    } finally {
      setSavingId(null);
    }
  };

  const handleCreate = async event => {
    event.preventDefault();
    if (!newName.trim()) {
      return;
    }

    try {
      const created = await createFooterSection({
        name: newName.trim(),
        order: sections.length,
      });
      setSections(previous => [...previous, created]);
      setNewName('');
      flash('Section added.');
    } catch (err) {
      const message =
        err.response && err.response.data && err.response.data.message;
      setError(message || 'Failed to add the section.');
    }
  };

  const handleDelete = async section => {
    if (!window.confirm(`Delete the “${section.name}” section?`)) {
      return;
    }

    try {
      await deleteFooterSection(section._id);
      setSections(previous =>
        previous.filter(item => item._id !== section._id),
      );
      flash('Section deleted.');
    } catch (err) {
      setError('Failed to delete the section.');
    }
  };

  const handleMoveSection = async (id, direction) => {
    const index = sections.findIndex(section => section._id === id);
    const target = index + direction;
    if (index === -1 || target < 0 || target >= sections.length) {
      return;
    }

    const reordered = move(sections, index, target);
    setSections(reordered);

    // Persist the new order of the two that swapped.
    try {
      await Promise.all(
        [reordered[index], reordered[target]].map((section, offset) =>
          updateFooterSection(section._id, {
            order: Math.min(index, target) + offset,
          }),
        ),
      );
    } catch (err) {
      setError('Failed to reorder sections.');
      load();
    }
  };

  return (
    <AdminLayout>
      <CmsAdminNav />
      <div className="container">
        <div className="row" style={{ marginBottom: '1rem' }}>
          <div className="col-sm-12">
            <h2>Footer menu</h2>
            <p>
              The link columns in the site footer. Each section is a heading
              with a list of links; a link can point to a site page (e.g.{' '}
              <code>/rules</code>, <code>/pages/about</code>) or an external
              address.
            </p>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {isLoading ? (
          <p>Loading footer menu…</p>
        ) : (
          <>
            {sections.map(section => (
              <SectionEditor
                key={section._id}
                section={section}
                saving={savingId === section._id}
                onSave={handleSave}
                onDelete={handleDelete}
                onMove={handleMoveSection}
              />
            ))}

            <form className="panel panel-default" onSubmit={handleCreate}>
              <div className="panel-heading">Add a section</div>
              <div className="panel-body">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    value={newName}
                    onChange={event => setNewName(event.target.value)}
                    placeholder="Section name (e.g. Company)"
                  />
                  <span className="input-group-btn">
                    <button type="submit" className="btn btn-primary">
                      Add section
                    </button>
                  </span>
                </div>
              </div>
            </form>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

AdminCmsFooterMenu.propTypes = {};
